/* 214 addendum 4 §25 [RULED 63 — Laurent, 04/10] — THE ONE WINDOW, THE SAME FOR EVERY IMPORT [ADVISOR — its shape].
   Before anything is written, an import that would lose what the app holds shows this: the totals first (« 11
   dossiers : 53 valeurs vidées, 17 retirés d'une liste »), then each matter (its ref) or client (his name) and each
   loss with the value it holds today, under its kind's own count line; three buttons — « Tout importer »,
   « Importer sans vider » (shown when the window lists an emptied value: an empty cell then leaves the value as it is,
   everything a cell states still applies) and « Annuler », which writes nothing. The plan is src/lib/importLoss.ts's;
   the importer that opens the window owns the writes. The words are one namespace, `import.loss.*`, each sentence a
   literal `t()` call with its English (the i18n gates read the call); « Annuler » is `common.cancel` (an existing key
   wherever one already says it); the plurals through the count machinery (208 §20).

   `import React` for react/react-in-jsx-scope (counted per element — 189 §E's remedy); a new file carries no ESLint
   warning: the callbacks take no parameter (no name to count unused), `t` is the translator's one shape.

   214 addendum 5 §28 — THE BUTTONS, AS THE APP'S (Laurent's glance of 05/10: « Importer sans vider » read as plain
   text, and « Tout importer » looked like any import in a window that archived every matter). The order stays —
   Annuler · Importer sans vider · Tout importer. */
import React from "react";
import { ModalLayout } from "./SharedUI";
import { FS, RAD } from "../lib/tokens";
import type { Translate } from "../lib/interestRefusal";
import { withCount } from "../lib/pluralVars"; // 209 §7 — a sentence's number, read once, as its placeholder and as `count`
import { LOSS_KINDS, takesRowsAway, totalsOf, type LossKind, type LossPlan } from "../lib/importLoss";

/* 214 addendum 5 §28 [ADVISOR — the precedent] — THE APP'S SECONDARY BUTTON IN A WINDOW FOOTER: the time entry window's
   extra save buttons, a `modal-save` with the accent tint (TimeEntryModal's « Save & Same Case »), its padding and size
   with it — through the tokens, where the precedent wrote the default accent's literals: the accent's soft ground
   `--as`, its soft hover `--ag` for the border, the accent `--ac` for the words (themeDerivation.ts derives both from
   the firm's own accent). */
const SECONDARY: React.CSSProperties = { padding: "8px 10px", fontSize: FS.s11, background: "var(--as)", color: "var(--ac)", border: "1px solid var(--ag)" };

/** The count sentence of a kind, in its language's plural. */
function kindSentence(t: Translate, kind: LossKind, count: number): string {
  const n = withCount(count, "count");
  switch (kind) {
    case "emptied":
      return t("import.loss.emptied", { ...n, fallback: `${String(count)} values emptied` });
    case "removed":
      return t("import.loss.removed", { ...n, fallback: `${String(count)} taken out of a list` });
    case "archived":
      return t("import.loss.archived", { ...n, fallback: `${String(count)} archived` });
    case "deleted":
      return t("import.loss.deleted", { ...n, fallback: `${String(count)} deleted` });
    default:
      return t("import.loss.unlinked", { ...n, fallback: `${String(count)} members unlinked` });
  }
}

interface Props {
  plan: LossPlan;
  t: Translate;
  /** the sentence naming the groups and their count: « 11 dossiers » (the caller's key — matters, clients, …) */
  groupsSentence: string;
  onAll: () => void;
  /** absent: no « Importer sans vider » button (the plan lists no emptied value) */
  onKeep?: () => void;
  onCancel: () => void;
  zIndex?: number;
}

export default function ImportLossDialog({ plan, t, groupsSentence, onAll, onKeep, onCancel, zIndex }: Readonly<Props>) {
  const totals = totalsOf(plan);
  const totalsLine = `${groupsSentence} : ${totals.map((x) => kindSentence(t, x.kind, x.count)).join(", ")}`;
  /* 214 addendum 5 §28 [ADVISOR] — a window that takes a row away (a matter archived, a client or a time entry deleted,
     a member's link with his payments) draws « Tout importer » as the app's destructive button, `modal-delete` (the
     footer primitive's own switch, SaveButton's `danger`); a window that only empties keeps the import's `modal-save`. */
  const destructive = takesRowsAway(plan);
  return (
    <ModalLayout
      title={t("import.loss.title", { fallback: "Before importing" })}
      onClose={onCancel}
      showX={false}
      closeOnOverlay={false}
      maxWidth={680}
      zIndex={zIndex}
      footer={
        <div className="modal-footer" data-import-loss-footer="">
          <button type="button" className="modal-cancel" data-import-loss-btn="cancel" onClick={onCancel}>
            {t("common.cancel", { fallback: "Cancel" })}
          </button>
          {onKeep && (
            <button type="button" className="modal-save" style={SECONDARY} data-import-loss-btn="keep" onClick={onKeep}>
              {t("import.loss.keep", { fallback: "Import without emptying" })}
            </button>
          )}
          <button type="button" className={destructive ? "modal-delete" : "modal-save"} data-import-loss-btn="all" onClick={onAll}>
            {t("import.loss.all", { fallback: "Import all" })}
          </button>
        </div>
      }
    >
      <div data-import-loss="" data-import-loss-groups={plan.groups.length}>
        <p style={{ fontSize: FS.s11, marginBottom: 6 }}>{t("import.loss.lead", { fallback: "This import would remove what the app holds:" })}</p>
        <p data-import-loss-totals="" style={{ fontSize: FS.s11_5, fontWeight: 600, marginBottom: 10 }}>{totalsLine}</p>
        <div style={{ maxHeight: 360, overflowY: "auto", border: "1px solid var(--br)", borderRadius: RAD.r6, padding: "6px 10px" }}>
          {plan.groups.map((g) => (
            <div key={g.key} data-loss-group={g.key} style={{ padding: "6px 0", borderBottom: "1px solid var(--br)" }}>
              <div style={{ fontSize: FS.s11, fontWeight: 600 }}>{g.label}</div>
              {LOSS_KINDS.filter((kind) => g.lines.some((l) => l.kind === kind)).map((kind) => (
                <div key={kind} style={{ margin: "2px 0 2px 10px" }}>
                  <div style={{ fontSize: FS.s9, fontWeight: 600, color: "var(--tm)", textTransform: "uppercase", letterSpacing: ".4px" }}>
                    {kindSentence(t, kind, g.lines.filter((l) => l.kind === kind).length)}
                  </div>
                  {g.lines
                    .filter((l) => l.kind === kind)
                    .map((l, i) => (
                      <div key={i} data-loss-line={kind} style={{ fontSize: FS.s10_5, lineHeight: 1.5, display: "flex", gap: 8 }}>
                        {l.field ? <span style={{ color: "var(--tm)", whiteSpace: "nowrap" }}>{l.field}</span> : null}
                        <span className="mono" style={{ overflowWrap: "anywhere" }}>{l.value}</span>
                      </div>
                    ))}
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    </ModalLayout>
  );
}
